import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Palette, RotateCcw } from "lucide-react";

// Predefined color themes with gradients and plain options
const COLOR_THEMES = {
  blue: {
    name: "Ocean Blue",
    gradient: "bg-gradient-to-r from-blue-600 via-blue-700 to-blue-800",
    plain: "bg-blue-600",
    preview: "#2563eb"
  },
  purple: {
    name: "Royal Purple",
    gradient: "bg-gradient-to-r from-purple-600 via-purple-700 to-purple-800",
    plain: "bg-purple-600",
    preview: "#9333ea"
  },
  green: {
    name: "Forest Green",
    gradient: "bg-gradient-to-r from-green-600 via-green-700 to-green-800",
    plain: "bg-green-600",
    preview: "#16a34a"
  },
  red: {
    name: "Ruby Red",
    gradient: "bg-gradient-to-r from-red-600 via-red-700 to-red-800",
    plain: "bg-red-600",
    preview: "#dc2626"
  },
  indigo: {
    name: "Deep Indigo",
    gradient: "bg-gradient-to-r from-indigo-600 via-indigo-700 to-indigo-800",
    plain: "bg-indigo-600",
    preview: "#4f46e5"
  },
  teal: {
    name: "Ocean Teal",
    gradient: "bg-gradient-to-r from-teal-600 via-teal-700 to-teal-800",
    plain: "bg-teal-600",
    preview: "#0d9488"
  },
  orange: {
    name: "Sunset Orange",
    gradient: "bg-gradient-to-r from-orange-600 via-orange-700 to-orange-800",
    plain: "bg-orange-600",
    preview: "#ea580c"
  },
  rose: {
    name: "Soft Rose",
    gradient: "bg-gradient-to-r from-rose-600 via-rose-700 to-rose-800",
    plain: "bg-rose-600",
    preview: "#e11d48"
  }
};

const ColorPicker = ({
  currentColor,
  onColorChange,
  onClose,
  disabled = false,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState("gradient");
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [isPositionReady, setIsPositionReady] = useState(false);
  const pickerRef = useRef(null);
  const buttonRef = useRef(null);

  // Update dropdown position when opening
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 8, // 8px gap
        left: rect.right + window.scrollX - 280 // Align right edge, 280px is dropdown width
      });
      setIsPositionReady(true);
    } else {
      setIsPositionReady(false);
    }
  }, [isOpen]);

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setIsOpen(false);
        if (onClose) onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen, onClose]);

  const handleColorSelect = (colorKey, style) => {
    const colorData = {
      type: style, // 'gradient' or 'plain'
      theme: colorKey,
      value: style === 'gradient' ? COLOR_THEMES[colorKey].gradient : COLOR_THEMES[colorKey].plain
    };

    onColorChange(colorData);
    setIsOpen(false);
    if (onClose) onClose();
  };

  const handleReset = () => {
    onColorChange(null); // Reset to default
    setIsOpen(false);
    if (onClose) onClose();
  };

  if (disabled) return null;

  return (
    <>
      {/* Color Picker Button */}
      <motion.button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={`group p-1.5 rounded-md hover:bg-white/20 transition-colors duration-200 nodrag ${className}`}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        title="Customize node color"
      >
        <Palette
          size={16}
          className="text-white/70 group-hover:text-white transition-colors"
        />
      </motion.button>

      {/* Color Picker Dropdown - Rendered via Portal */}
      {isOpen && isPositionReady && createPortal(
        <AnimatePresence>
          <motion.div
            ref={pickerRef}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className="fixed bg-white rounded-xl shadow-2xl border border-gray-200 p-4 min-w-[280px] nodrag"
            style={{
              zIndex: 9999,
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-800">Customize Color</h3>
              <button
                onClick={handleReset}
                className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                title="Reset to default"
              >
                <RotateCcw size={12} />
                Reset
              </button>
            </div>

            {/* Style Toggle */}
            <div className="flex bg-gray-100 rounded-lg p-1 mb-3">
              <button
                onClick={() => setSelectedStyle("gradient")}
                className={`flex-1 py-1.5 px-3 text-xs font-medium rounded-md transition-all ${
                  selectedStyle === "gradient"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                Gradient
              </button>
              <button
                onClick={() => setSelectedStyle("plain")}
                className={`flex-1 py-1.5 px-3 text-xs font-medium rounded-md transition-all ${
                  selectedStyle === "plain"
                    ? "bg-white text-gray-900 shadow-sm"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                Plain
              </button>
            </div>

            {/* Color Grid */}
            <div className="grid grid-cols-4 gap-2">
              {Object.entries(COLOR_THEMES).map(([colorKey, colorData]) => (
                <motion.button
                  key={colorKey}
                  onClick={() => handleColorSelect(colorKey, selectedStyle)}
                  className={`relative aspect-square rounded-lg border-2 border-gray-200 hover:border-gray-400 transition-all group ${
                    selectedStyle === 'gradient' ? colorData.gradient : colorData.plain
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  title={colorData.name}
                >
                  {/* Checkmark for selected color */}
                  {currentColor?.theme === colorKey && currentColor?.type === selectedStyle && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute inset-0 flex items-center justify-center"
                    >
                      <div className="w-4 h-4 bg-white rounded-full flex items-center justify-center shadow-lg">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      </div>
                    </motion.div>
                  )}

                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 rounded-lg transition-colors"></div>
                </motion.button>
              ))}
            </div>

            {/* Preview Text */}
            <div className="mt-3 text-center">
              <span className="text-xs text-gray-500">
                {selectedStyle === 'gradient' ? 'Gradient Style' : 'Plain Style'}
              </span>
            </div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

export default ColorPicker;